import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { FlexiAdvisorSettingsService } from '../advisor-settings/flexi-advisor-settings.service';
import { toIsoDate } from '../safety-rounds/safety-round.logic';
import { SafetyRoundLookupOption } from '../safety-rounds/safety-round.models';
import { ExposureActionPointRequest } from './exposure-register.models';
import { FlexiExposureRegisterService } from './exposure-register.service';

/**
 * Where the dialog stands:
 *  - ask:     at least one factor is Relevant; offer an Action plan agreement
 *  - confirm: nothing is Relevant; confirm and complete
 *  - form:    the agreement's measure, responsible and deadline
 *  - done:    completed; links to the report and the Action plan
 */
type CompleteStage = 'ask' | 'confirm' | 'form' | 'done';

/**
 * Completes an exposure assessment. When a health check is Relevant for any
 * factor, it first asks whether to add an agreement to set up health checks
 * to the Action plan; the action point is created by the backend in the same
 * transaction as the completion, the report and its Documents entry.
 */
@Component({
  selector: 'flexi-exposure-complete-dialog',
  templateUrl: './exposure-complete-dialog.component.html',
  styleUrls: ['./exposure-complete-dialog.component.scss'],
  standalone: false,
})
export class ExposureCompleteDialogComponent implements OnInit, OnDestroy {
  @Input({ required: true }) businessId = 0;
  @Input({ required: true }) assessmentId = 0;
  @Input() assessmentTitle = '';
  @Input() relevantCount = 0;
  @Input() relevantDepartmentCount = 0;
  advisors: SafetyRoundLookupOption[] = [];
  @Input() actionPointsUrl = '';
  /** The assessment is completed; the host re-reads it. */
  @Output() completed = new EventEmitter<void>();
  @Output() openReport = new EventEmitter<void>();
  @Output() backToOverview = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  visible = true;
  stage: CompleteStage = 'ask';
  completing = false;
  submitted = false;
  addedToPlan = false;

  /* ---- The Action plan agreement ---- */
  measure = '';
  responsibleUserId: number | null = null;
  deadline: Date | null = null;
  readonly minDeadline = new Date();

  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: FlexiExposureRegisterService,
    private advisorSettings: FlexiAdvisorSettingsService,
    private translate: TranslateService,
    private notifications: NotificationService,
  ) {}

  get headerKey(): string {
    switch (this.stage) {
      case 'form':
        return 'FLEXI_EXPOSURE.COMPLETE.FORM_TITLE';
      case 'done':
        return 'FLEXI_EXPOSURE.COMPLETE.DONE_TITLE';
      case 'confirm':
        return 'FLEXI_EXPOSURE.COMPLETE.CONFIRM_TITLE';
      default:
        return 'FLEXI_EXPOSURE.COMPLETE.ASK_TITLE';
    }
  }

  get responsibleName(): string {
    return this.advisors.find(user => user.value === this.responsibleUserId)?.label ?? '';
  }

  ngOnInit(): void {
    this.stage = this.relevantCount > 0 ? 'ask' : 'confirm';
    if (this.relevantCount > 0) {
      this.advisorSettings.businessAdvisors(this.businessId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: advisors => {
            this.advisors = advisors.filter(advisor => advisor.IsEligible)
              .map(advisor => ({ label: advisor.Name ?? '', value: advisor.UserId }));
          },
          error: () => this.notifications.error('FLEXI_EXPOSURE.COMPLETE.ADVISORS_UNAVAILABLE'),
        });
    }
    this.measure = this.translate.instant('FLEXI_EXPOSURE.COMPLETE.MEASURE_DEFAULT', { title: this.assessmentTitle });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showForm(): void {
    this.submitted = false;
    this.stage = 'form';
  }

  backToAsk(): void {
    this.submitted = false;
    this.stage = 'ask';
  }

  /** "No, just complete" and the plain confirmation. */
  completeWithoutActionPoint(): void {
    this.complete(null);
  }

  /** "Add and complete": the agreement needs a measure, a responsible and a deadline. */
  completeWithActionPoint(): void {
    this.submitted = true;
    if (!this.measure.trim() || !this.advisors.some(advisor => advisor.value === this.responsibleUserId) || !this.deadline) {
      return;
    }
    this.complete({
      Title: this.translate.instant('FLEXI_EXPOSURE.COMPLETE.ACTION_TITLE'),
      Description: this.measure.trim(),
      ResponsibleUserId: this.responsibleUserId,
      DueDate: toIsoDate(this.deadline),
    });
  }

  private complete(actionPoint: ExposureActionPointRequest | null): void {
    if (this.completing) {
      return;
    }
    this.completing = true;
    this.api
      .complete(this.businessId, this.assessmentId, { Confirmed: true, ActionPoint: actionPoint })
      .pipe(takeUntil(this.destroy$), finalize(() => (this.completing = false)))
      .subscribe({
        next: () => {
          this.addedToPlan = actionPoint !== null;
          this.stage = 'done';
          this.notifications.success('FLEXI_EXPOSURE.COMPLETE.COMPLETED_TOAST');
          this.completed.emit();
        },
        // The interceptor reports the failure (400: something changed underneath,
        // 409: completed elsewhere). The dialog stays open for a retry.
        error: () => undefined,
      });
  }

  visibleChanged(visible: boolean): void {
    if (!visible) {
      this.close();
    }
  }

  close(): void {
    if (this.completing) {
      return;
    }
    this.visible = false;
    this.closed.emit();
  }

  goToOverview(): void {
    this.visible = false;
    this.closed.emit();
    this.backToOverview.emit();
  }
}
